import styled from '@emotion/styled'

type OptionRowProps = {
  $isSelected: boolean
  $isWrongSelected: boolean
}

type OptionInputProps = {
  $status: 'default' | 'selected' | 'wrong'
}

type MessageTextProps = {
  isError?: boolean
}

export const Wrapper = styled.div`
  width: min(824px, calc(95vw - 50px));
  min-width: 360px;
  max-width: 824px;
  padding: 8px 4px;
  box-sizing: border-box;

  @media (max-width: 700px) {
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
  }
`

export const StyledTitle = styled.h2`
  margin: 0 0 20px;
  font-size: 28px;
  line-height: 34px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  color: ${({ theme }) => theme.colors.text.black};

  @media (max-width: 700px) {
    font-size: 24px;
    line-height: 30px;
  }
`

export const HintText = styled.p`
  box-sizing: border-box;
  width: 100%;
  margin: 0 0 28px;
  padding: 16px 24px;
  border-radius: 20px;
  background: #f2f6fa;
  color: #1d4e89;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;

  @media (max-width: 700px) {
    margin-bottom: 22px;
    padding: 14px 18px;
    font-size: 14px;
    line-height: 22px;
  }
`

export const QuestionCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 29px;
  width: 100%;
  margin-bottom: 29px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text.black};

  @media (max-width: 700px) {
    gap: 22px;
    margin-bottom: 24px;
  }
`

export const QuestionText = styled.p`
  margin: 0;
  width: 100%;
  max-width: 980px;
  box-sizing: border-box;
  font-size: 24px;
  line-height: 36px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.black};
  white-space: pre-wrap;

  @media (max-width: 700px) {
    font-size: 16px;
    line-height: 22px;
  }
`

export const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  /* left gutter so the wrong-answer status icon stays inside the modal */
  padding-left: 40px;

  @media (max-width: 700px) {
    padding-left: 0;
  }
`

export const MessageText = styled.p<MessageTextProps>`
  color: ${({ isError }) => (isError ? '#ff4d4f' : '#262626')};
  margin: -8px 0 0;
  font-size: 14px;
  line-height: 1.5;
`

export const OptionShell = styled.div`
  position: relative;
  align-self: stretch;
  width: 100%;
  box-sizing: border-box;
`

export const AnswerStateIcon = styled.span<{
  $visible: boolean
  $isCorrect: boolean
}>`
  position: absolute;
  left: -33px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  color: #ffffff;
  background: ${({ $isCorrect }) =>
    $isCorrect ? '#23af11' : '#ff4d4f'};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};

  @media (max-width: 700px) {
    left: auto;
    right: 14px;
    top: 14px;
    transform: none;
  }
`

export const OptionRow = styled.label<OptionRowProps>`
  display: flex;
  align-self: stretch;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 52px;
  padding: 12px 20px;
  box-sizing: border-box;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  transition:
    border-color 0.2s,
    background-color 0.2s;
  background-color: ${({ $isWrongSelected, $isSelected }) =>
    $isWrongSelected ? '#fff0f1' : $isSelected ? '#eaf3ff' : '#f5f7fa'};
  border-color: ${({ $isWrongSelected, $isSelected }) =>
    $isWrongSelected
      ? '#ffc7cd'
      : $isSelected
      ? '#c6dcf5'
      : '#e8ecf0'};

  ${({ $isWrongSelected, $isSelected }) =>
    !$isWrongSelected && !$isSelected
      ? `
          &:hover {
            border-color: #d9e8f8;
            background-color: #edf2f8;
          }
        `
      : ''}

  @media (max-width: 700px) {
    min-height: 50px;
    padding: 10px 14px;
  }
`

export const OptionInput = styled.input<OptionInputProps>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  margin: 0;
  border: 1px solid
    ${({ $status }) => {
      switch ($status) {
        case 'wrong':
          return '#ff4d4f'
        case 'selected':
          return '#1d4e89'
        default:
          return '#d9d9d9'
      }
    }};
  border-radius: 50%;
  background: #ffffff;
  cursor: pointer;
  flex-shrink: 0;

  &:checked {
    border-color: ${({ $status }) => {
      if ($status === 'wrong') return '#ff4d4f'
      if ($status === 'selected') return '#1d4e89'

      return '#1d4e89'
    }};
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $status }) => {
      if ($status === 'wrong') return '#ff4d4f'
      if ($status === 'selected') return '#1d4e89'

      return '#1d4e89'
    }};
    transform: translate(-50%, -50%);
  }

  &:disabled {
    cursor: default;
  }
`

export const OptionText = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.text.black};

  @media (max-width: 700px) {
    font-size: 14px;
    line-height: 20px;
  }
`

export const ButtonWrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: flex-start;
  width: 100%;

  ${({ $compact }) =>
    $compact
      ? `
        .ant-spin-nested-loading,
        .ant-spin-container {
          display: flex;
        }

        button {
          box-sizing: border-box;
          min-width: 119px;
          min-height: 44px;
          height: 44px;
          padding: 0 20px;
          border-radius: 24px;
          border: 1px solid #1d4e89;
          background: #1d4e89;
          color: #ffffff;
          justify-content: center;
          font-size: 12px;
          line-height: 16px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.1em;

          &:hover {
            background: #1d4e89;
            color: #ffffff;
            border: 1px solid #1d4e89;
            box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.2);
          }

          &:disabled {
            border: 1px solid #d9d9d9;
            background: #f5f5f5;
            color: #a7a7a7;
            box-shadow: none;

            &:hover {
              border: 1px solid #d9d9d9;
              background: #f5f5f5;
              color: #a7a7a7;
              box-shadow: none;
            }
          }
        }
      `
      : ''}
`
